import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ApplicationHostTypeService,
  HistoryService,
  ReferenceDataConstants,
  ReferenceDataLabels,
  ReferenceDataOptionLists,
  ReferenceDataService,
  UserPermissions,
  UserSessionService
} from 'rome-library';
import { filter } from 'rxjs/operators';
import { States } from '../../../shared';
import { CookieService } from '../../services/cookie.service';
import { LoginService } from '../../services/login.service';
import { PopUpService } from '../../services/pop-up.service';
import { StateService } from '../../services/state.service';
import { TokenStore } from '../../services/token.store';
import { UserNotificationService } from '../../services/user-notification.service';
import { UserService } from '../../services/user.service';
import { HeaderService } from './../../services/header.service';
import { FaqContextStore } from './../../../interfaceTypes/sharedFeatures/faq-dialog/services/faq-context-store.service';

@UntilDestroy()
@Component({
  selector: 'boi-vo-application-header',
  templateUrl: './application-header.component.html'
})
export class ApplicationHeaderComponent implements OnInit {
  public readonly referenceDataLabels = ReferenceDataLabels;
  public readonly userPermissions = UserPermissions;
  public readonly states = States;

  public headerTitle: string;
  public isCookieWarningVisible = false;
  public amlSearch = false;
  public isleftMenuOpened = false;

  constructor(
    private readonly loginService: LoginService,
    private readonly userSessionService: UserSessionService,
    public readonly userService: UserService,
    private readonly router: Router,
    public readonly applicationHostTypeService: ApplicationHostTypeService,
    public readonly stateService: StateService,
    private readonly referenceDataService: ReferenceDataService,
    private readonly cookieService: CookieService,
    private readonly historyService: HistoryService,
    private readonly headerService: HeaderService,
    private readonly userNotificationService: UserNotificationService,
    private readonly tokenStore: TokenStore,
    private readonly popUpService: PopUpService,
    private readonly faqContextStore: FaqContextStore
  ) {}

  ngOnInit(): void {
    this.displayCookieBanner();
    this.setUnauthenticatedHeaderTitle();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        untilDestroyed(this)
      )
      .subscribe((data: NavigationEnd) => {
        this.isleftMenuOpened = false;
      });
  }

  public isUserProfileCompleteAndValid(): boolean {
    return this.userService.getUser().ProfileComplete && !this.tokenStore.getUserContext().IsFirmStartingInTheFuture;
  }

  public toogleLeftMenu(): void {
    this.isleftMenuOpened = !this.isleftMenuOpened;
  }

  public goToCaseHub(): void {
    this.stateService.goToCaseHub();
  }

  public logout(): void {
    this.loginService
      .logout(States.Logout)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.userSessionService.stopSession();
        this.userNotificationService.stop();
        this.historyService.clearHistory();
      });
  }

  public goToUserProfile(): void {
    void this.router.navigate([
      this.applicationHostTypeService.getCode(),
      this.stateService.getUserProfileState(),
      this.userService.getUser().UserId
    ]);
  }

  public goToAccessController(): void {
    void this.router.navigate([
      this.applicationHostTypeService.getCode(),
      this.stateService.getAccessControllerState()
    ]);
  }

  private displayCookieBanner(): void {
    const displayCookiesDisclaimerConfig = this.referenceDataService.getClientConfigurationValue(
      ReferenceDataConstants.DisplayCookiesDisclaimer
    );
    const areCookiesAllowed = displayCookiesDisclaimerConfig === ReferenceDataOptionLists.AnswerTypeYes;

    if (areCookiesAllowed) {
      this.cookieService
        .getCookiesBannerVisibility()
        .pipe(untilDestroyed(this))
        .subscribe((shouldDisplayCookies) => {
          this.isCookieWarningVisible = shouldDisplayCookies;
        });
    }
  }

  public goToHelpCenter(): void {
    const src = `${this.applicationHostTypeService.getCode()}/${this.stateService.getHelpCenterState()}`;

    const params = `?action=${this.faqContextStore.FaqEndpoint}&context=${this.faqContextStore.FaqContext}`;

    this.popUpService.openContentPopUp(src + params, '_blank');
  }

  private setUnauthenticatedHeaderTitle(): void {
    this.headerService
      .getHeaderTitle()
      .pipe(untilDestroyed(this))
      .subscribe((title) => {
        this.headerTitle = title;
      });
  }

  public closeOnlineOfferSession(): void {
    this.headerService.closeSessionEvent$.emit();
  }

  public goToLogin(): void {
    void this.router.navigate([this.applicationHostTypeService.getCode(), States.Login]);
  }

  public refreshMakePayment(): void {
    this.headerService.refreshMakePaymentEvent$.emit();
  }
}
